import React, { useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiImage, FiSearch } from "react-icons/fi";

import {
  searchMedia,
  toAbsolutePublicUrl,
} from "../../../../services/mediaLibraryServices";

import AdminButton from "./AdminButton";
import AdminDrawer from "./AdminDrawer";
import AdminField from "./AdminField";

export default function AdminMediaPickerDrawer({
  open,
  onClose,
  onSelect,
  title = "Choisir une image",
  subtitle = "Sélectionnez une image dans la photothèque.",
  defaultCategory = "",
}) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(defaultCategory);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (open) {
      setCategory(defaultCategory);
      setPage(1);
    }
  }, [open, defaultCategory]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const fetchMedia = async () => {
      try {
        setLoading(true);

        const res = await searchMedia({
          q: q.trim() || undefined,
          category: category.trim() || undefined,
          type: "image",
          page,
          limit,
          sort: "created_at",
          dir: "DESC",
        });

        if (cancelled) return;

        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotalPages(Number(res?.totalPages || 1));
      } catch (error) {
        console.error("Erreur searchMedia picker :", error);

        if (!cancelled) {
          setItems([]);
          setTotalPages(1);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchMedia();

    return () => {
      cancelled = true;
    };
  }, [open, q, category, page]);

  const handleSelect = (media) => {
    if (!media) return;
    onSelect?.(media);
  };

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      icon={FiImage}
      size="full"
      zIndex="z-[100]"
      footer={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
            Page {page} / {totalPages}
          </p>

          <div className="flex gap-2">
            <AdminButton
              icon={FiChevronLeft}
              variant="secondary"
              disabled={page <= 1 || loading}
              onClick={() => setPage((previous) => Math.max(previous - 1, 1))}
            >
              Précédent
            </AdminButton>

            <AdminButton
              rightIcon={FiChevronRight}
              variant="secondary"
              disabled={page >= totalPages || loading}
              onClick={() =>
                setPage((previous) => Math.min(previous + 1, totalPages))
              }
            >
              Suivant
            </AdminButton>

            <AdminButton variant="dark" onClick={onClose}>
              Fermer
            </AdminButton>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-4 rounded-[1.7rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900 md:grid-cols-[1fr_260px]">
          <AdminField label="Rechercher">
            <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
              <FiSearch className="text-slate-400" />

              <input
                value={q}
                onChange={(event) => {
                  setPage(1);
                  setQ(event.target.value);
                }}
                placeholder="Titre, tags, nom de fichier..."
                className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 dark:text-slate-100"
              />
            </div>
          </AdminField>

          <AdminField
            label="Catégorie"
            help="Laissez vide pour afficher toutes les catégories."
          >
            <input
              value={category}
              onChange={(event) => {
                setPage(1);
                setCategory(event.target.value);
              }}
              placeholder="events, news, projets..."
              className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
            />
          </AdminField>
        </div>

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
            {Array.from({ length: 10 }).map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="aspect-[4/3] animate-pulse bg-slate-200 dark:bg-slate-800" />
                <div className="space-y-2 p-3">
                  <div className="h-3 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
                  <div className="h-3 w-1/2 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex min-h-[260px] items-center justify-center rounded-[1.7rem] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-900">
            <div>
              <FiImage className="mx-auto text-5xl text-slate-300 dark:text-slate-600" />
              <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
                Aucune image trouvée
              </h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Vérifiez les filtres ou ajoutez des images dans la médiathèque.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
            {items.map((media) => {
              const url = toAbsolutePublicUrl(media.url);

              return (
                <button
                  key={media.id}
                  type="button"
                  onClick={() => handleSelect(media)}
                  className="group overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-xl hover:shadow-slate-200/70 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-sky-500/30 dark:hover:shadow-black/20"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-800">
                    <img
                      src={url}
                      alt={media.alt_text || media.title || media.file_name}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  </div>

                  <div className="p-3">
                    <p className="line-clamp-2 text-sm font-bold text-slate-900 dark:text-white">
                      {media.title || media.file_name || "Image sans titre"}
                    </p>

                    <p className="mt-1 line-clamp-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                      {media.category || "Sans catégorie"}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </AdminDrawer>
  );
}